/**
 * Changing what a device is licensed for.
 *
 * Owner-only on the server. This form is offered to everyone and refused by the
 * API for anyone else — the alternative is hiding it based on a role decoded from
 * an unverified token, which would be treating a client-side guess as a control.
 *
 * Reducing capacity below what is in use is allowed and removes nothing. The
 * device simply cannot grow until it is back under the limit; deleting cameras to
 * fit a smaller licence is a decision for a person, not a side effect of an edit.
 */

import { useState, type FormEvent } from 'react';

import { ApiError, updateEntitlement } from './api.ts';

/** yyyy-mm-dd for a date input, or '' when there is no expiry. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toISOString().slice(0, 10);
}

export function EditEntitlement({
  deviceId,
  maxCameras,
  retentionDays,
  expiresAt,
  camerasInUse,
  onChanged,
}: {
  deviceId: string;
  maxCameras: number | null;
  retentionDays: number | null;
  expiresAt: string | null;
  camerasInUse: number;
  onChanged: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [cameras, setCameras] = useState(String(maxCameras ?? ''));
  const [retention, setRetention] = useState(String(retentionDays ?? 0));
  const [expiry, setExpiry] = useState(toDateInput(expiresAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);

    // Parsed strictly. Number('') is 0 and Number('4 cameras') is NaN, so a lax
    // check would send a licence count nobody chose.
    if (!/^\d+$/.test(cameras.trim()) || Number.parseInt(cameras, 10) < 1) {
      setError('Camera capacity must be a whole number of at least 1.');
      return;
    }
    if (!/^\d+$/.test(retention.trim())) {
      setError('Cloud retention must be zero or more days.');
      return;
    }

    setBusy(true);
    try {
      await updateEntitlement(deviceId, {
        maxCameras: Number.parseInt(cameras, 10),
        cloudEventRetentionDays: Number.parseInt(retention, 10),
        // Blank means no expiry, which is a real state rather than a missing
        // value — sent as null so the server can tell it from "leave it alone".
        expiresAt: expiry === '' ? null : new Date(`${expiry}T23:59:59Z`).toISOString(),
      });
      setOpen(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('Only owners can change what a device is licensed for.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not update the entitlement.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
        Edit entitlement
      </button>
    );
  }

  const requested = Number.parseInt(cameras, 10);
  const wouldBeOver = Number.isInteger(requested) && requested < camerasInUse;

  return (
    <form className="form entitlement-form" onSubmit={(e) => void submit(e)}>
      <div className="field">
        <label htmlFor={`cap-${deviceId}`}>Camera capacity</label>
        <input
          id={`cap-${deviceId}`}
          value={cameras}
          inputMode="numeric"
          onChange={(e) => setCameras(e.target.value)}
        />
        <span className="field__hint">{camerasInUse} in use.</span>
      </div>

      {/* Said before saving, not discovered afterwards. Allowed, but the operator
          should know what it does. */}
      {wouldBeOver ? (
        <p className="notice notice--warn">
          This is below the {camerasInUse} cameras already configured. Nothing is removed and they
          keep recording — the device just cannot have more added until it is back under the limit.
        </p>
      ) : null}

      <div className="field">
        <label htmlFor={`ret-${deviceId}`}>Cloud event retention (days)</label>
        <input
          id={`ret-${deviceId}`}
          value={retention}
          inputMode="numeric"
          onChange={(e) => setRetention(e.target.value)}
        />
        {/* Cloud storage is what destroys VSaaS margin, so zero is the sane
            default and raising it should feel like a decision. */}
        <span className="field__hint">Zero keeps everything on the device only.</span>
      </div>

      <div className="field">
        <label htmlFor={`exp-${deviceId}`}>Expires</label>
        <input
          id={`exp-${deviceId}`}
          type="date"
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
        />
        <span className="field__hint">Leave blank for no expiry.</span>
      </div>

      <div className="form__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
